import React from 'react';
import { RouterProvider } from 'react-router-dom';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

import router from 'routes';
import ThemeCustomization from 'themes';
import Locales from 'components/Locales';
import ScrollTop from 'components/ScrollTop';
import Notistack from 'components/third-party/Notistack';
import { AuthProvider } from 'contexts/AuthProvider';

import { reactPlugin } from './config/appInsights';
import { telemetryService } from './api/telemetryService';

import { ErrorBoundary } from './components/ErrorBoundary';

function initializeTelemetry() {
  try {
    telemetryService.trackEvent('App.Initialized', {
      environment: import.meta.env.MODE,
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      timestamp: new Date().toISOString()
    });

    setupGlobalErrorHandlers();

    console.info('Telemetry initialized successfully');
  } catch (error) {
    console.error('Failed to initialize telemetry:', error);
  }
}

function setupGlobalErrorHandlers() {
  window.addEventListener('unhandledrejection', (event) => {
    telemetryService.trackException(new Error(`Unhandled Promise Rejection: ${event.reason}`), SeverityLevel.Error, {
      reason: event.reason,
      type: 'UnhandledPromiseRejection'
    });

    if (import.meta.env.MODE === 'development') {
      console.error('Unhandled Promise Rejection:', event.reason);
    }
  });

  window.addEventListener('error', (event) => {
    telemetryService.trackException(event.error || new Error(event.message), SeverityLevel.Error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      type: 'GlobalError'
    });

    if (import.meta.env.MODE === 'development') {
      console.error('Global Error:', event.error || event.message);
    }
  });
}

export function setupUserSession(userId: string, accountId?: string) {
  telemetryService.setUserId(userId, accountId);
}

export function cleanupUserSession() {
  telemetryService.clearUserId();
  telemetryService.flush();
}

const App = () => {
  React.useEffect(() => {
    initializeTelemetry();

    return () => {
      telemetryService.flush();
    };
  }, []);

  return (
    <ErrorBoundary>
      <AppInsightsContext.Provider value={reactPlugin}>
        <ThemeCustomization>
          <Locales>
            <ScrollTop>
              <AuthProvider>
                <Notistack>
                  <RouterProvider router={router} />
                </Notistack>
              </AuthProvider>
            </ScrollTop>
          </Locales>
        </ThemeCustomization>
      </AppInsightsContext.Provider>
    </ErrorBoundary>
  );
};

export default App;
